import parse from 'html-react-parser';
import { useState, useEffect } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { AssetType, SlideType } from '@/utils/types/enums';

import { QuestionSlideProps, Asset, SlideItemType } from '../../types';
import { getAssetsByRefs, getAssetUrl } from '../../utils/asset-loader';
import { getGridColumns } from '../../utils/grid-columns';

export const QuestionMultipleSlide = ({
  slide,
  assets,
  assetsBaseUrl,
  correctAnswerIds,
  onAnswerSelected,
  correctAttempts: answeredQuestions,
  incorrectAttempts,
  caseConfig,
}: QuestionSlideProps) => {
  const [selectedAnswerIds, setSelectedAnswerIds] = useState<Set<string>>(new Set());
  const [isValidated, setIsValidated] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  const caseId = caseConfig.id;

  // Get min and max choices from slide config
  const minChoices =
    slide.type === SlideType.QUESTION_MULTIPLE ? slide.minChoices : correctAnswerIds.length;
  const maxChoices =
    slide.type === SlideType.QUESTION_MULTIPLE ? slide.maxChoices : correctAnswerIds.length;

  // Reset states when slide changes
  useEffect(() => {
    const currentPreviousAnswer = answeredQuestions.get(slide.id) || [];

    if (currentPreviousAnswer) {
      // Restore previous answers
      if (Array.isArray(currentPreviousAnswer)) {
        setSelectedAnswerIds(new Set(currentPreviousAnswer));
      } else {
        // Handle legacy single answer format
        setSelectedAnswerIds(new Set([currentPreviousAnswer]));
      }
      setIsValidated(true);
      setShowFeedback(true);
    } else {
      // New question - reset everything
      setSelectedAnswerIds(new Set());
      setIsValidated(false);
      setShowFeedback(false);
    }
  }, [slide.id, answeredQuestions]);

  const handleAnswerToggle = (answerId: string) => {
    if (isValidated) return; // Block after validation

    const newSelected = new Set(selectedAnswerIds);
    if (newSelected.has(answerId)) {
      newSelected.delete(answerId);
    } else {
      if (slide.type === SlideType.QUESTION_MULTIPLE && newSelected.size >= maxChoices) {
        return; // Limit reached
      }
      newSelected.add(answerId);
    }
    setSelectedAnswerIds(newSelected);
  };

  const handleValidate = () => {
    const selectedArray = Array.from(selectedAnswerIds);

    // Check if all selected answers are correct
    const allCorrect = selectedArray.every((id) => correctAnswerIds.includes(id));
    const allCorrectSelected = correctAnswerIds.every((id) => selectedArray.includes(id));

    const isCorrect = allCorrect && allCorrectSelected;

    setIsValidated(true);
    setShowFeedback(true);

    if (isCorrect) {
      // Save correct answer (as array)
      void onAnswerSelected(selectedArray.join(','), true);
    } else {
      // Save incorrect answer (recordAnswer handles it in CaseRenderer)
      void onAnswerSelected(selectedArray.join(','), false);
    }
  };

  const questionIncorrectAttempts = new Set(incorrectAttempts?.[slide.id] || []);

  // Filter items to get only answer items
  const answerItems = slide.items.filter((item) => item.type === SlideItemType.ANSWER_SELECTABLE);

  // Get linked assets for answers
  const linkedAssets = getAssetsByRefs(
    answerItems.flatMap((item) => item.assetRefs || []),
    assets
  );

  const gridCols = getGridColumns(answerItems.length);

  return (
    <div className="space-y-6">
      {/* Question Title */}
      {slide.title && (
        <div className="mb-6 text-xl font-semibold text-livelinx-text">{parse(slide.title)}</div>
      )}

      {/* Info items */}
      {slide.items
        .filter((item) => item.type === SlideItemType.INFO)
        .map((item, idx) => (
          <div key={idx} className="mb-4 text-base text-livelinx-grey200">
            {parse(item.title || item.description || '')}
          </div>
        ))}

      {/* Answer Grid */}
      <div className={cn('grid gap-4', gridCols)}>
        {answerItems.map((item) => {
          const isCorrectAnswer = correctAnswerIds.includes(item.id);
          const isSelectedAnswer = selectedAnswerIds.has(item.id);
          const isIncorrectAttempt = questionIncorrectAttempts.has(item.id);

          const showCorrectFeedback = isValidated && isCorrectAnswer;
          const showIncorrectFeedback = isValidated && !isCorrectAnswer && isSelectedAnswer;

          // Get linked assets for this answer
          const itemAssets = linkedAssets.filter((asset: Asset) =>
            item.assetRefs?.includes(asset.id || '')
          );

          return (
            <div
              key={item.id}
              onClick={() => !isValidated && handleAnswerToggle(item.id)}
              className={cn(
                'relative rounded-lg border-2 p-4 transition-all duration-200',
                isValidated && 'cursor-not-allowed',
                !isValidated && 'cursor-pointer hover:shadow-md',
                showCorrectFeedback && 'border-green-500 bg-green-50 ring-2 ring-green-500',
                showIncorrectFeedback && 'border-red-500 bg-red-50 opacity-60 ring-2 ring-red-500',
                !isValidated && isSelectedAnswer && 'border-livelinx-blue300 bg-livelinx-blue50',
                !isValidated && !isSelectedAnswer && 'border-livelinx-lightgray100'
              )}
            >
              {/* Checkbox */}
              <div className="absolute right-4 top-4">
                <Checkbox
                  checked={isSelectedAnswer || showCorrectFeedback}
                  feedbackState={(() => {
                    if (showCorrectFeedback) return 'correct';
                    if (showIncorrectFeedback) return 'incorrect';
                    return 'default';
                  })()}
                  disabled={isValidated}
                />
              </div>

              {/* Answer Content */}
              <div className="pr-12">
                {item.title && (
                  <div className="mb-2 text-base text-livelinx-text">{parse(item.title)}</div>
                )}
                {item.description && (
                  <div className="mt-1 text-sm text-livelinx-grey200">
                    {parse(item.description)}
                  </div>
                )}

                {/* Linked Assets */}
                {itemAssets.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {itemAssets.map((asset: Asset, idx: number) => (
                      <div key={idx}>
                        {asset.type === AssetType.IMAGE && (
                          <img
                            src={getAssetUrl(asset, assetsBaseUrl)}
                            alt={asset.title || ''}
                            className="max-h-40 rounded-md object-cover"
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Validation Button */}
      {!isValidated && (
        <div className="mt-6 flex justify-center">
          <Button
            onClick={handleValidate}
            disabled={selectedAnswerIds.size < minChoices || selectedAnswerIds.size > maxChoices}
            className="px-8 py-3"
          >
            Validate ({selectedAnswerIds.size}/{maxChoices})
          </Button>
        </div>
      )}

      {/* Feedback Message */}
      {showFeedback && (
        <div
          className={cn(
            'mt-4 rounded-lg p-4 text-center font-semibold',
            correctAnswerIds.every((id) => selectedAnswerIds.has(id)) &&
              Array.from(selectedAnswerIds).every((id) => correctAnswerIds.includes(id))
              ? 'bg-green-100 text-green-800'
              : 'bg-red-100 text-red-800'
          )}
        >
          {correctAnswerIds.every((id) => selectedAnswerIds.has(id)) &&
          Array.from(selectedAnswerIds).every((id) => correctAnswerIds.includes(id))
            ? 'Correct! Well done!'
            : 'Incorrect. Please review the correct answers above.'}
        </div>
      )}
    </div>
  );
};
